import React from "react";
import styled from "styled-components";
import * as Router from "next/router";
import axios from "axios";
import DOMPurify from "dompurify";

import {
  ClubSupportQuestions,
  DigitalSupportQuestions,
} from "../utils/supportQuestions";
import * as u from "../utils";
import * as gtag from "../utils/gtag";
import contactTypes from "../utils/contactTypes";
import * as UserContext from "../components/UserContext";
import * as T from "../components/Typography";
import * as Icons from "../components/Icons";
import Button from "../components/Button";
import ContentPanels from "../components/ContentPanels";
import Input from "../components/Input";
import Link from "../components/Link";
import SelectSearch from "../components/SelectSearch";
import TextArea from "../components/TextArea";
import DropdownRow from "../components/Profile/DropdownRow";

export default function Contact(props) {
  const userInfo = React.useContext(UserContext.Context);
  const isLoggedIn = !!userInfo.id;
  const router = Router.useRouter();
  const [name, setName] = React.useState(userInfo.name || "");
  const [phone, setPhone] = React.useState();
  const [email, setEmail] = React.useState(userInfo.email || "");
  const [queryType, setQueryType] = React.useState(router.query.type ?? "");
  const [message, setMessage] = React.useState("");
  const [formState, setFormState] = React.useState("");
  const [nameError, setNameError] = React.useState(false);
  const [emailError, setEmailError] = React.useState(false);
  const [queryError, setQueryError] = React.useState(false);
  const [messageError, setMessageError] = React.useState(false);

  React.useEffect(() => {
    if (formState === "success") clearForm();
  }, [formState]);
  React.useEffect(() => {
    if (router.query?.type) setQueryType(router.query?.type);
  }, [router.query]);

  const MessageIcon =
    formState === "success" ? (
      <Icons.Tick color={u.colours.teal} />
    ) : (
      <Icons.Close color={u.colours.errorRed} />
    );

  const getFAQs = (questions) => {
    return (
      <>
        {questions.map((item) => (
          <DropdownRow title={item.title} noGap maxHeight="50" key={item.title}>
            <T.CS>{item.answer}</T.CS>
          </DropdownRow>
        ))}
      </>
    );
  };

  return (
    <S.Container>
      <S.Panels>
        <ContentPanels>
          <SP.Map src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2483.005242382985!2d-0.144512849014692!3d51.513119818006544!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4876058346e15335%3A0x84ee2705fdde309a!2sAllBright%20Mayfair%20Members&#39;%20Club!5e0!3m2!1sen!2suk!4v1620737535971!5m2!1sen!2suk" />
          <SP.Content color={u.colours.shellOverlay}>
            <TT.H1S>The AllBright Townhouse</TT.H1S>
            <div>
              {props.openingTimes.map((time) => (
                <T.CXL key={time}>{time}</T.CXL>
              ))}
            </div>
            <div>
              <T.CXL>Easter Hours</T.CXL>
              <T.CXL>Fri, March 29th - Closed</T.CXL>
              <T.CXL>Mon, April 1st - Closed</T.CXL>
            </div>
            <div>
              <T.CXL>24-26 Maddox Street</T.CXL>
              <T.CXL>Mayfair</T.CXL>
              <T.CXL>London, W1S 1PP</T.CXL>
              <T.CXL>020 3817 3340</T.CXL>
            </div>
            <div>
              <br />
              <T.CL weight="700">Contact us:</T.CL>
              <br />
              <T.CL>
                Townhouse reception:{" "}
                <T.A href="mailto:mayfairreception@allbright.co">
                  mayfairreception@allbright.co
                </T.A>
              </T.CL>
              <br />
              <T.CL>
                Events:{" "}
                <T.A href="mailto:events@allbright.co">events@allbright.co</T.A>
              </T.CL>
            </div>
            <div>
              <T.CL>
                Virtual memberships enquiries:{" "}
                <T.A href="mailto:digital@allbright.co">
                  digital@allbright.co
                </T.A>
              </T.CL>
              <br />
              <T.CL>
                Membership plans enquiries:{" "}
                <T.A href="mailto:membership@allbright.co">
                  membership@allbright.co
                </T.A>
              </T.CL>
            </div>
          </SP.Content>
        </ContentPanels>
      </S.Panels>
      <S.FAQs id="club-faqs">
        <S.Header>
          <T.H2Rox>Club FAQs</T.H2Rox>
          <T.CXXL>Support for Mayfair Club members</T.CXXL>
        </S.Header>
        {getFAQs(ClubSupportQuestions)}
      </S.FAQs>
      <S.FAQs id="digital-faqs">
        <S.Header>
          <T.H2Rox>Digital FAQs</T.H2Rox>
          <T.CXXL>Support for AllBright Digital accounts</T.CXXL>
        </S.Header>
        {getFAQs(DigitalSupportQuestions)}
      </S.FAQs>
      <S.Header center id="contact-form">
        <T.H2Rox>Get in touch with us</T.H2Rox>
        <T.CXXL>We are here to help you</T.CXXL>
      </S.Header>
      <S.InputWrap>
        <SelectSearch
          hint={isLoggedIn ? "Reason for contact *" : "Query type *"}
          name="query-type"
          value={queryType}
          onSelect={(e) => onSelectQuery(e)}
          onClick={() => setQueryError(false)}
          opts={Object.keys(contactTypes)}
          isInvalid={queryError}
        />
        {formState && MessageIcon}
        {SUBMIT_MESSAGES[formState]}
      </S.InputWrap>
      {queryType && queryType !== "Private Hire Events" && (
        <S.Contact onSubmit={handleSubmit} noValidate>
          {!isLoggedIn && (
            <>
              <Input
                hint="Full name *"
                name="name"
                value={name}
                onChange={(e) =>
                  updateField(e, setName, nameError, setNameError)
                }
                isInvalid={nameError}
              />
              <Input
                hint="Phone"
                name="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <Input
                hint="Email address *"
                name="email"
                value={email}
                onChange={(e) =>
                  updateField(e, setEmail, emailError, setEmailError)
                }
                isInvalid={emailError}
                type="email"
              />
            </>
          )}
          <TextArea
            hint="Your Message *"
            name="message"
            value={message}
            onChange={(e) =>
              updateField(e, setMessage, messageError, setMessageError)
            }
            isInvalid={messageError}
          />
          <SC.Privacy>
            <T.CL>
              By submitting this form indicates you agree with our{" "}
              <Link href="/privacy" isTextAnimation>
                Privacy Policy
              </Link>
              .
            </T.CL>
          </SC.Privacy>
          <br />
          <Button text="Submit" isWide />
        </S.Contact>
      )}
    </S.Container>
  );

  function onSelectQuery(e) {
    if (e === "Private Hire Events")
      router.replace("https://allbright.tripleseat.com/party_request/24934");
    setQueryType(e);
  }

  function updateField(event, setField, error, setError) {
    setField(event.target.value);
    if (error) setError(false);
    if (formState.length) setFormState("");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const formErrors =
      !name.trim().length ||
      !emailRegex.test(email) ||
      !queryType ||
      !message.trim().length;

    if (formErrors) {
      checkInputs();
      return;
    }

    gtag.event({
      name: gtag.GA_EVENT.CUSTOMER_CONTACT,
      category: "Contact",
      label: "Top of Funnel",
      value: queryType,
    });

    try {
      const url = "/api/user.contact-allbright";
      const body = {
        email,
        message: DOMPurify.sanitize(message),
        name: DOMPurify.sanitize(name),
        phone: DOMPurify.sanitize(phone),
        type: queryType,
        source: "non-member on web",
      };
      await axios.post(url, body);
      setFormState("success");
    } catch (error) {
      setFormState("error");
    }

    function checkInputs() {
      if (!name.trim().length) {
        setNameError(true);
        setFormState("invalid");
      }
      if (!emailRegex.test(email)) {
        setEmailError(true);
        setFormState("invalid");
      }
      if (!queryType) {
        setQueryError(true);
        setFormState("invalid");
      }
      if (!message.trim().length) {
        setMessageError(true);
        setFormState("invalid");
      }
    }
  }

  function clearForm() {
    setName("");
    setPhone("");
    setEmail("");
    setQueryType();
    setMessage("");
  }
}

const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const SUBMIT_MESSAGES = {
  success: " Thank you, we will get back to you shortly",
  invalid: " Please check this form has what we need to get back to you",
  error: " An unknown error occurred, please try again",
};

export async function getStaticProps() {
  const axios = require("axios");
  const config = require("../config");

  try {
    const { data } = (
      await axios.post(config.CONTENTFUL_GENERAL, getGraphQuery())
    ).data;
    const openingTimes =
      data.clubTimesCollection.items?.[0]?.times?.split("\n") || [];

    return { props: { openingTimes }, revalidate: 3600 };
  } catch {
    const openingTimes = [
      "Mon, 8AM-6PM",
      "Tue-Fri, 8AM-9PM",
      "Sat-Sun, Closed",
    ];

    return { props: { openingTimes }, revalidate: 3600 };
  }

  function getGraphQuery() {
    return {
      query: `{
        clubTimesCollection {
          items {times}
        }
      }`,
    };
  }
}

const S = {};

S.Container = styled.div`
  position: relative;
  padding: 0 0 40px 0;
`;

S.Header = styled.div`
  margin: 40px 0 20px;

  ${(p) => p.center && "text-align: center;"}

  @media ${u.device.tablet} {
    margin: 40px 0 40px;
  }

  @media ${u.device.desktop} {
    margin: 80px 0 30px;
  }
`;

S.InputWrap = styled.div`
  margin: 0 auto;
  padding: 20px 16px 0;
  text-align: center;
  @media (min-width: 535px) {
    max-width: 570px;
    padding: 0 40px;
  }
`;

S.Contact = styled.form`
  margin: 0 auto;
  padding: 20px 16px 0;
  text-align: center;
  @media (min-width: 535px) {
    max-width: 570px;
    padding: 0 40px;
  }
`;

S.Panels = styled.div`
  padding: 0 16px 20px;
  margin: 0 auto;
  max-width: 1440px;
`;

S.FAQs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin: 0 auto;
  padding: 0 20px;
  max-width: 500px;
`;

const SC = {};

SC.Privacy = styled.label``;

const SP = {};

SP.Map = styled.iframe`
  border: 0;
  bottom: 0;
  height: 100%;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
  width: 100%;
`;

SP.Content = styled.article`
  padding: 40px;
  height: 100%;
  display: flex;
  flex-direction: column;
  text-align: left;
  > * {
    margin: 0;
    margin-right: auto;
  }
  gap: 15px;
  @media (min-width: 530px) {
    padding: 80px;
  }
`;

const TT = {};

TT.H1B = styled(T.H1)`
  color: white;
  margin-bottom: 10px;
  @media ${u.device.tablet} {
    font-size: 4.8rem;
    line-height: 6rem;
  }
`;

TT.H1S = styled(T.H1Rox)`
  font-size: 3.2rem;
  line-height: 3.8rem;
  margin-bottom: 20px;
`;

TT.CXXL = styled(T.CXXL)`
  margin-bottom: 20px;
  max-width: ${(p) => (p.isHero ? "790px" : "1126px")};
  ${(p) => p.isBold && "font-weight: bold;"}
  ${(p) => p.isHero && "color: white;"}
  @media ${u.device.tablet} {
    font-size: 2.4rem;
    line-height: 3.6rem;
  }
  @media ${u.device.desktop} {
    margin-bottom: 40px;
  }
`;
